import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Slide, ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthExpirationProvider, useAuthExpiration } from './context/AuthExpirationContext';
import { DateProvider } from './context/DateContext';
import { ThemeModeProvider } from './context/ThemeModeContext';
import { LoginModalProvider } from './context/LoginModalContext';
import { SponsorCodeProvider } from './context/SponsorCodeContext';
import { EcomTypeProvider } from './context/EcomTypeContext';
import { CssBaseline } from '@mui/material';

// Component imports
import Loader from './components/loader';
import ErrorBoundary from './components/errorBoundary';

// Route imports
import { ProtectedRoute } from './routes/ProtectedRoute';
import { ProtectedRouteList } from './routes/ProtectedRouteList';
import { RenderRoutes } from './routes/RenderRoutes';

// Network status imports
import { useGetNetworkStatus } from './hooks/useNetworkStatus';
import { OfflineContainer } from './components/offlineContainer';
import { setAuthExpirationHandler } from './configurations/axiosConfig';

// Theme imports
import AppTheme from './theme/AppTheme';
import './App.css';
import ProtectedLayout from './layout/private/ProtectedLayout';

const Logout = lazy(() => import('./pages/private/Logout'));

// Public pages
const PublicLayout = lazy(() => import('./layout/public/PublicLayout'));
const HomePage = lazy(() => import('./pages/public/home/HomePage'));
const Register = lazy(() => import('./pages/public/auth/Register'));

const ExploreAllProducts = lazy(() => import('./pages/public/products/ExploreAllProducts'));
const NewArrivals = lazy(() => import('./pages/public/products/NewArrivals'));
const FeaturedProducts = lazy(() => import('./pages/public/products/FeaturedProducts'));
const ProductDetail = lazy(() => import('./pages/public/products/ProductDetail'));

const LoginModal = lazy(() => import('./pages/public/auth/LoginModal'));
const SponsorCodeModal = lazy(() => import('./pages/public/auth/SponsorCodeModal'));
const PageNotFound = lazy(() => import('./components/pageNotFound/PageNotFound'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

const AxiosInterceptorSetup = () => {
  const { showExpirationDialog } = useAuthExpiration();

  useEffect(() => {
    setAuthExpirationHandler(showExpirationDialog);
  }, [showExpirationDialog]);

  return null;
};

const App = () => {
  return (
    <ThemeModeProvider>
      <AppTheme>
        <CssBaseline enableColorScheme />
        <QueryClientProvider client={queryClient}>
          <ToastContainer
            position="top-center"
            autoClose={3000}
            limit={3}
            transition={Slide}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <DateProvider>
            <EcomTypeProvider>
            <LoginModalProvider>
              <SponsorCodeProvider>
                <AuthProvider>
                  <AuthExpirationProvider>
                    <Router>
                      <Suspense fallback={<Loader />}>
                        <AxiosInterceptorSetup />
                        <ErrorBoundary>
                          <LoginModal />
                          <SponsorCodeModal />
                          <AppRoutes />
                        </ErrorBoundary>
                      </Suspense>
                    </Router>
                  </AuthExpirationProvider>
                </AuthProvider>
              </SponsorCodeProvider>
            </LoginModalProvider>
            </EcomTypeProvider>
          </DateProvider>
        </QueryClientProvider>
      </AppTheme>
    </ThemeModeProvider>
  );
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  const { isOnline } = useGetNetworkStatus();

  if (isAuthenticated === undefined) return <Loader />;
  if (!isOnline) return <OfflineContainer />;

  return (
    <Routes>
      <Route path="/logout" element={<Logout />} />

      {/* PUBLIC ROUTES — shared layout with constant header/footer */}
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/explore-all" element={<ExploreAllProducts />} />
        <Route path="/new-arrivals" element={<NewArrivals />} />
        <Route path="/featured-products" element={<FeaturedProducts />} />
        <Route path="/product/:id" element={<ProductDetail />} />
      </Route>

      {/* PROTECTED ROUTES — redirect unauthenticated users to / */}
      <Route element={<ProtectedRoute><ProtectedLayout /> </ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        {RenderRoutes(ProtectedRouteList)}

      </Route>

      {/* GLOBAL CATCH-ALL — shown for any unmatched route */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default App;
